import { useState, useMemo } from 'react';

import PropTypes from 'prop-types';

import Table from '#globalComponents/Table/Table';
import { useScopedSelection } from '#globalHooks/useScopedSelection';

import { useDealsList } from '../../../models/deals/queries';
import { usePowerDialerStore } from '../../../stores/powerDialerStore';

import DealBulkActions from './DealBulkActions';
import { buildDealListColumns } from './dealListColumns';
import DealScopeActions from './DealScopeActions';
import { mapDealToDialerQueue } from './mapDealToDialerQueue';

const PAGE_SIZE = 50;

const ListView = ({
	pipelineId,
	stages,
	filters = {},
	filtersKey,
	page,
	onPageChange,
	onOpenDeal,
	aircallConnected,
}) => {
	const [sortKey, setSortKey] = useState('dealValue');
	const [sortDir, setSortDir] = useState('desc');

	const dialerActive = usePowerDialerStore((s) => s.active);
	const dialerQueue = usePowerDialerStore((s) => s.queue);
	const dialerIndex = usePowerDialerStore((s) => s.currentIndex);
	const activeDealId = dialerActive ? dialerQueue[dialerIndex]?.dealId : null;
	const startDialer = usePowerDialerStore((s) => s.start);

	const queryFilters = useMemo(
		() => ({ pipelineId, page, pageSize: PAGE_SIZE, orderBy: sortKey, orderDir: sortDir, ...filters }),
		[pipelineId, page, sortKey, sortDir, filters],
	);

	const { data, isLoading } = useDealsList(queryFilters);
	const deals = useMemo(() => data?.deals ?? [], [data?.deals]);
	const totalDeals = data?.total ?? 0;
	const totalPages = data?.totalPageCount ?? 1;

	const selection = useScopedSelection(deals, {
		totalCount: totalDeals,
		filters,
		resetKey: `${pipelineId}|${filtersKey}`,
		entityLabel: { singular: 'Deal', plural: 'Deals', gender: 'm' },
	});

	const stageMap = useMemo(() => {
		const m = {};
		const sorted = [...stages].sort((a, b) => a.stageOrder - b.stageOrder);
		sorted.forEach((s, i) => {
			m[s.id] = { ...s, _colorIndex: i };
		});
		return m;
	}, [stages]);

	const handleSort = (key) => {
		if (sortKey === key) {
			setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
		} else {
			setSortKey(key);
			setSortDir('desc');
		}
		onPageChange(1);
	};

	const handleStartDialerFromDeal = (dealId) => {
		const q = deals.map((d) => mapDealToDialerQueue(d, stageMap)).filter(Boolean);
		const idx = q.findIndex((item) => item.dealId === dealId);
		if (idx >= 0) startDialer(q, pipelineId, idx);
	};

	const columns = buildDealListColumns({
		stageMap,
		aircallConnected,
		dialerActive,
		onStartDialerFromDeal: handleStartDialerFromDeal,
	});

	return (
		<Table
			layoutKey="crmDeals"
			rows={deals}
			columns={columns}
			isLoading={isLoading}
			emptyLabel="Keine Deals gefunden"
			onRowClick={(deal) => deal.crmCompanyId && onOpenDeal?.(deal.crmCompanyId)}
			getRowProps={(deal) =>
				deal.id === activeDealId
					? { className: 'bg-primary-100/60 border-l-[3px] border-l-primary-500' }
					: undefined
			}
			selection={selection.tableSelection}
			actions={{
				label: selection.actionScopeLabel,
				buttons: (
					<DealScopeActions
						pipelineId={pipelineId}
						selection={selection}
						pageDeals={deals}
						stageMap={stageMap}
						aircallConnected={aircallConnected}
					/>
				),
			}}
			sort={{ sortKey, sortDir, onSortChange: handleSort }}
			pagination={{
				currentPage: page,
				totalPages,
				onPageChange,
				totalItems: totalDeals,
				pageSize: PAGE_SIZE,
			}}
			bulkBar={<DealBulkActions pipelineId={pipelineId} selection={selection} />}
		/>
	);
};

ListView.propTypes = {
	pipelineId: PropTypes.string.isRequired,
	stages: PropTypes.array.isRequired,
	/** Deal list query params (see dealFiltersToApiParams). */
	filters: PropTypes.object,
	filtersKey: PropTypes.string.isRequired,
	page: PropTypes.number.isRequired,
	onPageChange: PropTypes.func.isRequired,
	onOpenDeal: PropTypes.func,
	aircallConnected: PropTypes.bool,
};

export default ListView;
